// Fixed authenticationGate undefined error - replaced with isAuthenticationValid
import { useEffect, useRef, useCallback, useState, useMemo } from 'react';
import i18n from '@/i18n';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';
import { useAuth } from '@/contexts/AuthContext';
import { useLogging } from '@/shared/hooks/useLogging';
import type { Message } from '@/types/message-types';
import { MessageDataService } from '@/services/MessageDataService';
import { ToastDeduplicator, createDedupedToast } from '@/utils/toastDeduplication';

interface UseRealtimeMessagesProps {
  conversationId: string;
  enabled?: boolean;
  onMessageReceived: (message: Message) => void;
  onMessageUpdated: (messageId: string, updates: Partial<Message>) => void;
  onLoadMessages: () => void;
  onSilentRefresh?: () => void;
  aiMode?: 'manual' | 'auto' | 'suggestions';
  onAIProcessingStart?: (messageContent?: string) => void;
  tenantId?: string; // Use tenant ID from AuthContext
  allConversationIds?: string[]; // Customer mode: subscribe to ALL conversation messages
  customerId?: string; // Customer mode: stable channel name
}

export function useRealtimeMessages({
  conversationId,
  enabled = true,
  onMessageReceived,
  onMessageUpdated,
  onLoadMessages,
  onSilentRefresh,
  aiMode,
  onAIProcessingStart,
  tenantId, // Use tenant ID from AuthContext
  allConversationIds,
  customerId: customerIdProp
}: UseRealtimeMessagesProps) {
  const { toast } = useToast();
  const { user } = useAuth();
  const { info, error, debug, warn } = useLogging('whatsapp');
  const toastDeduplicator = ToastDeduplicator.getInstance();
  const dedupedToast = createDedupedToast();
  
  // CRITICAL: Singleton subscription management to prevent subscription storms
  const activeSubscriptions = useRef<Map<string, unknown>>(new Map());
  const channelRef = useRef<unknown>(null);
  const tenantChannelRef = useRef<unknown>(null);
  const pollIntervalRef = useRef<NodeJS.Timeout | null>(null);
  const healthCheckRef = useRef<NodeJS.Timeout | null>(null);
  
  // Generate stable instance ID based on conversation to prevent duplicates
  const instanceIdRef = useRef(`realtime-msg-${conversationId}-${Date.now()}`);
  const lastMessageTime = useRef<number>(Date.now());
  const recentlySentMessages = useRef<Set<string>>(new Set());

  // [FIX: MESSAGE-MIXING] Store current conversationId in ref to prevent stale closures
  const conversationIdRef = useRef<string | undefined>(conversationId);

  // Customer mode: ref for allConversationIds (used in callbacks without stale captures)
  const allConversationIdsRef = useRef<string[] | undefined>(allConversationIds);
  // Stable key for subscription deps (avoids array referential inequality)
  const allConversationIdsKey = useMemo(
    () => allConversationIds ? [...allConversationIds].sort().join(',') : '',
    [allConversationIds]
  );

  const [tenantSchema, setTenantSchema] = useState<string | null>(null);
  const schemaInitialized = useRef(false);
  const [subscriptionHealthy, setSubscriptionHealthy] = useState(false);
  const [retryCount, setRetryCount] = useState(0);
  const [authError, setAuthError] = useState<string | null>(null);
  const [isTransitioning, setIsTransitioning] = useState(false);
  const [circuitBreakerOpen, setCircuitBreakerOpen] = useState(false);
  const maxRetries = 3; // Reduced back to prevent excessive retries
  const circuitBreakerThreshold = 5; // Open circuit after 5 consecutive failures
  const authListenerRef = useRef<unknown>(null);

  // Initialize hook only once per conversation with enhanced tracking
  const initializationRef = useRef(false);
  const cleanupExecutedRef = useRef(false);

  // Stable authentication check - no dependencies to prevent cascading re-renders
  const isAuthenticationValid = useCallback(() => {
    // Only log authentication failures, not successful checks to prevent spam
    if (!user) {
      console.log(`[${instanceIdRef.current}] 🚫 No authenticated user - blocking realtime operations`);
      return false;
    }

    // Platform admins can use Realtime without tenant_id (for testing purposes)
    const isPlatformAdmin = user.user_metadata?.is_platform_admin === true;

    if (!tenantId && !isPlatformAdmin) {
      console.log(`[${instanceIdRef.current}] 🚫 No tenant ID from AuthContext - blocking realtime operations`);
      return false;
    }

    if (!tenantId && isPlatformAdmin) {
      console.log(`[${instanceIdRef.current}] ✅ Platform admin detected - allowing Realtime for testing`);
    }

    return true;
  }, []); // No dependencies - use current values directly from closure

  // Setup functions with refs to avoid circular dependencies
  const setupFallbackPollingRef = useRef<() => void>();
  const reconnectSubscriptionRef = useRef<() => void>();
  const setupHealthCheckRef = useRef<() => void>();
  const setupRealtimeSubscriptionRef = useRef<() => void>();
  const setupTenantBroadcastSubscriptionRef = useRef<() => void>();

  // Callback refs to avoid dependency issues
  const onMessageReceivedRef = useRef(onMessageReceived);
  const onMessageUpdatedRef = useRef(onMessageUpdated);
  const onLoadMessagesRef = useRef(onLoadMessages);
  const onSilentRefreshRef = useRef(onSilentRefresh);
  const onAIProcessingStartRef = useRef(onAIProcessingStart);

  // [FIX: MESSAGE-MIXING] Update conversationId ref when it changes
  useEffect(() => {
    conversationIdRef.current = conversationId;
  }, [conversationId]);

  // Keep allConversationIds ref in sync
  useEffect(() => {
    allConversationIdsRef.current = allConversationIds;
    if (allConversationIds && allConversationIds.length > 100) {
      console.warn(`[${instanceIdRef.current}] ⚠️ allConversationIds exceeds 100 (${allConversationIds.length}) — Supabase in() filter hard limit`);
    }
  }, [allConversationIds]);

  // Update callback refs when functions change
  useEffect(() => {
    onMessageReceivedRef.current = onMessageReceived;
  }, [onMessageReceived]);

  useEffect(() => {
    onMessageUpdatedRef.current = onMessageUpdated;
  }, [onMessageUpdated]);

  useEffect(() => {
    onLoadMessagesRef.current = onLoadMessages;
  }, [onLoadMessages]);

  useEffect(() => {
    onSilentRefreshRef.current = onSilentRefresh;
  }, [onSilentRefresh]);

  useEffect(() => {
    onAIProcessingStartRef.current = onAIProcessingStart;
  }, [onAIProcessingStart]);

  // Add recently sent message tracking
  const addRecentlySentMessage = useCallback((messageId: string) => {
    recentlySentMessages.current.add(messageId);
    // Clean up after 10 seconds to prevent memory leaks
    setTimeout(() => {
      recentlySentMessages.current.delete(messageId);
    }, 10000);
  }, []);

  // Data quality tracking
  const [dataQualityScore, setDataQualityScore] = useState(100);
  const [poorQualityCount, setPoorQualityCount] = useState(0);
  const maxPoorQualityThreshold = 3;

  // Error throttling to prevent console spam
  const errorThrottleRef = useRef<Map<string, number>>(new Map());
  const throttleError = useCallback((errorKey: string, errorMessage: string, throttleMs = 5000) => {
    const now = Date.now();
    const lastLogged = errorThrottleRef.current.get(errorKey) || 0;
    if (now - lastLogged > throttleMs) {
      console.error(errorMessage);
      errorThrottleRef.current.set(errorKey, now);
    }
  }, []);
  
  // Connection stability tracking
  const [connectionErrors, setConnectionErrors] = useState<string[]>([]);
  const [lastSuccessfulConnection, setLastSuccessfulConnection] = useState<number>(Date.now());
  const [fallbackPollingActive, setFallbackPollingActive] = useState(false);
  const connectionHealthRef = useRef({ corsErrors: 0, websocketErrors: 0, totalErrors: 0 });
  const circuitBreakerRef = useRef({ isOpen: false, lastFailure: 0, failures: 0 });
  const maxConnectionErrors = 8; // Increased tolerance
  const circuitBreakerTimeout = 120000; // 2 minutes (less aggressive)
  
  // Track consecutive failures to implement smarter backoff
  const consecutiveFailuresRef = useRef(0);
  const lastToastShownRef = useRef(0);
  const lastReconnectTimeRef = useRef(0);
  const TOAST_DEBOUNCE_INTERVAL = 30000; // 30 seconds between same type of toasts

  // CRITICAL: Enhanced aggressive channel cleanup to prevent subscription storms
  const aggressiveChannelCleanup = useCallback(() => {
    // Remove all channels for this conversation/customer
    const keys = [
      `messages-${conversationId}`,
      ...(customerIdProp ? [`messages-customer-${customerIdProp}`] : [])
    ];
    for (const key of keys) {
      const existingChannel = activeSubscriptions.current.get(key);
      if (existingChannel) {
        try {
          supabase.removeChannel(existingChannel);
          activeSubscriptions.current.delete(key);
        } catch (error) {
          console.warn('Error removing existing channel:', error);
        }
      }
    }
    
    // Clean up refs
    if (channelRef.current) {
      try {
        supabase.removeChannel(channelRef.current);
        channelRef.current = null;
      } catch (error) {
        console.warn('Error removing channelRef:', error);
      }
    }
    
    if (tenantChannelRef.current) {
      try {
        supabase.removeChannel(tenantChannelRef.current);
        tenantChannelRef.current = null;
      } catch (error) {
        console.warn('Error removing tenantChannelRef:', error);
      }
    }
    
    // Clear intervals
    if (pollIntervalRef.current) {
      clearInterval(pollIntervalRef.current);
      pollIntervalRef.current = null;
    }
    
    if (healthCheckRef.current) {
      clearInterval(healthCheckRef.current);
      healthCheckRef.current = null;
    }
  }, [conversationId, customerIdProp]);

  // Initialize hook only once per conversation
  useEffect(() => {
    if (!initializationRef.current) {
      aggressiveChannelCleanup();
      initializationRef.current = true;
    }
  }, [aggressiveChannelCleanup]);

  // Validate message data quality and track score
  const validateMessageData = useCallback((rawMessage: Record<string, unknown>) => {
    if (!rawMessage) return { isValid: false, score: 0, issues: ['No data'] };
    
    let score = 100;
    const issues: string[] = [];

    // Only penalize truly critical missing fields — not optional ones like media_url
    if (!rawMessage.id) { score -= 40; issues.push('Missing ID'); }
    if (!rawMessage.conversation_id) { score -= 40; issues.push('Missing conversation_id'); }
    if (!rawMessage.sender_type) { score -= 30; issues.push('Missing sender_type'); }

    return { isValid: score >= 70, score, issues };
  }, []);

  const handleRealtimeMessage = useCallback((payload: Record<string, unknown>) => {
    const rawMessage = payload.new;
    lastMessageTime.current = Date.now();
    
    console.log(`[${instanceIdRef.current}] 🎯 REALTIME MESSAGE RECEIVED:`, {
      messageId: rawMessage?.id,
      conversationId: rawMessage?.conversation_id,
      expectedConversationId: conversationId,
      senderType: rawMessage?.sender_type,
      content: rawMessage?.content?.substring(0, 50) + '...',
      timestamp: new Date().toISOString(),
      payloadType: payload.eventType,
      isCorrectConversation: rawMessage?.conversation_id === conversationId,
      fullPayload: payload // For debugging
    });
    
    // CRITICAL: If this is an AI message we're expecting, log it extensively
    if (rawMessage?.sender_type === 'ai_agent_automatic') {
      console.log(`[${instanceIdRef.current}] 🤖 AI MESSAGE DETECTED VIA REAL-TIME:`, {
        messageId: rawMessage.id,
        content: rawMessage.content?.substring(0, 100),
        timestamp: rawMessage.created_at,
        conversationId: rawMessage.conversation_id,
        sender: rawMessage.sender_type,
        isExpectedConversation: rawMessage.conversation_id === conversationId
      });
    }
    
    // Validate data quality first
    const validation = validateMessageData(rawMessage);
    
    console.log(`[${instanceIdRef.current}] 📊 Message validation result:`, validation);
    
    // Update data quality tracking
    setDataQualityScore(validation.score);
    
    if (!validation.isValid) {
      console.error(`[${instanceIdRef.current}] ❌ INVALID MESSAGE DATA:`, {
        issues: validation.issues,
        score: validation.score,
        rawMessage
      });
      
      throttleError(`poor-quality-${instanceIdRef.current}`, `[${instanceIdRef.current}] ❌ Poor data quality: ${validation.issues.join(', ')}`, 10000);
      warn('Poor message data quality detected', {
        messageId: rawMessage?.id,
        issues: validation.issues,
        score: validation.score
      });
      
      // Track poor quality events
      setPoorQualityCount(prev => {
        const newCount = prev + 1;
        
        // Force subscription restart if too many poor quality messages
        if (newCount >= maxPoorQualityThreshold) {
          console.warn(`[${instanceIdRef.current}] 🔄 Too many poor quality messages, restarting subscription`);
          setTimeout(() => {
            if (reconnectSubscriptionRef.current) {
              reconnectSubscriptionRef.current();
            }
          }, 1000);
          return 0; // Reset counter
        }
        
        return newCount;
      });
      
      // Trigger silent refresh for data recovery
      if (onSilentRefresh) {
        onSilentRefresh();
      } else {
        onLoadMessages();
      }
      return;
    }
    
    // Reset poor quality counter on successful validation
    setPoorQualityCount(0);
    setSubscriptionHealthy(true);
    
    // Update last message time to indicate real-time is working
    lastMessageTime.current = Date.now();
    console.log(`[${instanceIdRef.current}] ✅ REAL-TIME WORKING: Message received, subscription healthy`);

    // Extract validated message data
    const messageId = rawMessage.id;
    const messageConversationId = rawMessage.conversation_id;
    const senderType = rawMessage.sender_type;
    const senderId = rawMessage.sender_id;
    const content = rawMessage.content;

    console.log(`[${instanceIdRef.current}] 🔍 Message filtering check:`, {
      messageConversationId,
      expectedConversationId: conversationId,
      matches: messageConversationId === conversationId,
      senderType,
      senderId,
      currentUserId: user?.id
    });

    // [FIX: MESSAGE-MIXING] Layer 1: Filter using ref to prevent stale closure
    if (allConversationIdsRef.current && allConversationIdsRef.current.length > 0) {
      // Customer mode: accept messages from any of the customer's conversations
      if (!allConversationIdsRef.current.includes(messageConversationId)) {
        console.log(`[${instanceIdRef.current}] 🚫 [Layer 1 REJECT] Message filtered - not in customer conversations`, {
          messageConversation: messageConversationId,
          customerConversationCount: allConversationIdsRef.current.length
        });
        return;
      }
    } else if (conversationIdRef.current && messageConversationId !== conversationIdRef.current) {
      // Single conversation mode: existing behavior
      console.log(`[${instanceIdRef.current}] 🚫 [Layer 1 REJECT] Message filtered - conversation mismatch`, {
        messageConversation: messageConversationId,
        currentConversation: conversationIdRef.current
      });
      return;
    }

    // Skip processing if this is a recently sent message (prevent double processing)
    if (recentlySentMessages.current.has(messageId)) {
      console.log(`[${instanceIdRef.current}] ⏭️ Message filtered out - recently sent`);
      return;
    }

    // Skip processing own messages sent by this user to prevent conflicts
    if (user && senderId === user.id && senderType === 'human_agent') {
      console.log(`[${instanceIdRef.current}] ⏭️ Message filtered out - own agent message`);
      return;
    }

    console.log(`[${instanceIdRef.current}] ✅ Message passed all filters, processing...`);

    try {
      console.log(`[${instanceIdRef.current}] 🔍 PROCESSING MESSAGE:`, {
        messageId: rawMessage.id,
        senderType: rawMessage.sender_type,
        content: rawMessage.content?.substring(0, 50),
        whatsappId: rawMessage.external_id,
        timestamp: rawMessage.created_at,
        isAIMessage: rawMessage.sender_type === 'ai_agent_automatic'
      });

      const cleanMessage = MessageDataService.cleanSingleMessage(rawMessage, instanceIdRef.current);
      
      console.log(`[${instanceIdRef.current}] 📤 Calling onMessageReceived with cleaned message:`, {
        messageId: cleanMessage.id,
        senderType: cleanMessage.sender_type,
        content: cleanMessage.content?.substring(0, 50) + '...',
        isAIMessage: cleanMessage.sender_type === 'ai_agent_automatic'
      });

      console.log(`[${instanceIdRef.current}] 📤 CALLING onMessageReceived for message:`, {
        id: cleanMessage.id,
        sender_type: cleanMessage.sender_type,
        content: cleanMessage.content?.substring(0, 50),
        isAIMessage: cleanMessage.sender_type === 'ai_agent_automatic'
      });
      
      onMessageReceivedRef.current(cleanMessage);

      console.log(`[${instanceIdRef.current}] ✅ Message successfully passed to onMessageReceived callback`);
      
      // Message processed successfully - real-time is working, ensure backup polling is stopped
      if (pollIntervalRef.current) {
        clearInterval(pollIntervalRef.current);
        pollIntervalRef.current = null;
        setFallbackPollingActive(false);
        console.log(`[${instanceIdRef.current}] 📡 BACKUP POLLING STOPPED - message processed via real-time`);
      }
      
      // Reset connection health indicators - real-time is working perfectly
      setSubscriptionHealthy(true);
      setRetryCount(0);
      setCircuitBreakerOpen(false);
      setConnectionErrors([]);
      console.log(`[${instanceIdRef.current}] ✅ REAL-TIME HEALTHY - all indicators reset`);
      
      // Signal to other components that real-time is working  
      if (onSilentRefresh) {
        // Don't call silent refresh now - we just processed the message via real-time
        console.log(`[${instanceIdRef.current}] 📡 Skipping silent refresh - message arrived via real-time`);
      }

      // Show toast for customer messages only
      if (cleanMessage.sender_type === 'customer') {
        console.log(`[${instanceIdRef.current}] 💬 Customer message - showing toast and triggering AI`);
        
        toast({
          title: i18n.t('conversations:realtime.newMessage'),
          description: `Message: ${cleanMessage.content?.substring(0, 50)}${cleanMessage.content && cleanMessage.content.length > 50 ? '...' : ''}`,
          duration: 3000,
        });

        // AI processing is now handled entirely by RealtimeChat component
        console.log(`[${instanceIdRef.current}] 📋 Customer message received - AI processing delegated to RealtimeChat`);
      }
    } catch (err) {
      console.error(`[${instanceIdRef.current}] ❌ CRITICAL ERROR processing message:`, err);
      throttleError(`message-processing-${instanceIdRef.current}`, `[${instanceIdRef.current}] ❌ Error processing real-time message: ${err}`, 10000);
      error('Failed to process realtime message', {
        messageId: rawMessage?.id,
        error: err
      });
      // Only trigger reload for critical processing errors, not minor issues
      if (error instanceof Error && error.message.includes('CRITICAL')) {
        if (onSilentRefresh) {
          onSilentRefresh();
        } else {
          onLoadMessages();
        }
      }
    }
  }, [conversationId, onLoadMessages, toast, user, validateMessageData, onSilentRefresh, throttleError, info, warn, error, aiMode]);

  const handleRealtimeUpdate = useCallback((payload: Record<string, unknown>) => {
    const updatedMessage = payload.new;
    const oldMessage = payload.old;

    // Filter by conversation(s) if specified
    if (allConversationIdsRef.current && allConversationIdsRef.current.length > 0) {
      if (!allConversationIdsRef.current.includes(updatedMessage.conversation_id)) return;
    } else if (conversationId && updatedMessage.conversation_id !== conversationId) {
      return;
    }

    try {
      const cleanMessage = MessageDataService.cleanSingleMessage(updatedMessage, instanceIdRef.current);
      
      // Extract only the changed fields
      const updates: Partial<Message> = {};
      (Object.keys(cleanMessage) as Array<keyof Message>).forEach(key => {
        if (cleanMessage[key] !== oldMessage?.[key]) {
          (updates as Record<string, unknown>)[key] = cleanMessage[key];
        }
      });

      if (Object.keys(updates).length > 0) {
        onMessageUpdated(cleanMessage.id, updates);
      }
    } catch (error) {
      throttleError(`update-processing-${instanceIdRef.current}`, `[${instanceIdRef.current}] ❌ Error processing real-time update: ${error}`, 10000);
    }
  }, [conversationId, onMessageUpdated, throttleError]);

  // Get tenant schema on mount - WITH AUTHENTICATION GATE
  const getTenantSchema = useCallback(async () => {
    if (schemaInitialized.current) return;

    // Check authentication before any Supabase operations
    if (!isAuthenticationValid()) {
      console.log(`[${instanceIdRef.current}] 🔒 getTenantSchema blocked - authentication invalid`);
      return;
    }

    try {
      // First check if user is authenticated
      const { data: { session } } = await supabase.auth.getSession();
      if (!session?.user) {
        throttleError(`auth-session-${instanceIdRef.current}`, `[${instanceIdRef.current}] ❌ No authenticated session found for tenant schema lookup`, 15000);
        return;
      }

      let schema: string | null = null;

      // PRIORITY 1: If tenantId prop provided (test mode), look up schema directly
      if (tenantId) {
        console.log(`[${instanceIdRef.current}] 🎯 Using override tenantId: ${tenantId}`);
        const { data: tenant, error: tenantError } = await supabase
          .from('tenants')
          .select('schema_name')
          .eq('id', tenantId)
          .eq('status', 'active')
          .single();

        if (tenantError) {
          console.error(`[${instanceIdRef.current}] ❌ Error fetching tenant schema:`, tenantError);
        } else {
          schema = tenant?.schema_name || null;
        }
      } else {
        // PRIORITY 2: Use RPC function for normal authenticated users
        const { data: rpcSchema, error: rpcError } = await supabase.rpc('get_user_tenant_schema');

        if (rpcError) {
          throttleError(`tenant-schema-${instanceIdRef.current}`, `[${instanceIdRef.current}] ❌ Error calling get_user_tenant_schema: ${rpcError}`, 15000);
          return;
        }
        schema = rpcSchema;
      }

      if (schema) {
        console.log(`[${instanceIdRef.current}] 📊 Found tenant schema: ${schema}`);
        setTenantSchema(schema);
        schemaInitialized.current = true;
      } else {
        throttleError(`no-schema-${instanceIdRef.current}`, `[${instanceIdRef.current}] ⚠️ No tenant schema found`, 15000);
      }
    } catch (error) {
      throttleError(`schema-fetch-${instanceIdRef.current}`, `[${instanceIdRef.current}] ❌ Error getting tenant schema: ${error}`, 15000);
    }
  }, [throttleError, isAuthenticationValid, tenantId]);

  // Enhanced fallback polling WITH AUTHENTICATION GATE
  const setupFallbackPolling = useCallback(() => {
    // Check authentication before any polling operations
    if (!isAuthenticationValid()) {
      console.log(`[${instanceIdRef.current}] 🔒 setupFallbackPolling blocked - authentication invalid`);
      return;
    }
    
    if (pollIntervalRef.current) {
      clearInterval(pollIntervalRef.current);
    }

    setFallbackPollingActive(true);
    
    // Conservative polling with dynamic interval based on real-time health
    const getPollingInterval = () => {
      if (subscriptionHealthy) return 60000; // 1 minute if real-time is working
      return 15000; // 15 seconds if real-time is broken
    };
    
    pollIntervalRef.current = setInterval(() => {
      console.log(`📡 BACKUP POLLING: Running ${subscriptionHealthy ? 'healthy' : 'unhealthy'} check`);
      
      // Use silent refresh to avoid UI disruption
      if (onSilentRefresh) {
        onSilentRefresh();
      } else {
        onLoadMessages();
      }
      
      // Check if we can restore real-time connection with longer timeout
      if (Date.now() - circuitBreakerRef.current.lastFailure > circuitBreakerTimeout) {
        circuitBreakerRef.current.isOpen = false;
        circuitBreakerRef.current.failures = 0;
        
        if (setupRealtimeSubscriptionRef.current) {
          console.log(`📡 BACKUP POLLING: Attempting to restore real-time subscription`);
          setupRealtimeSubscriptionRef.current();
        }
      }
      
      // Update interval dynamically
      if (pollIntervalRef.current) {
        clearInterval(pollIntervalRef.current);
        const interval = getPollingInterval();
        const currentFunction = arguments.callee as () => void;
        pollIntervalRef.current = setInterval(currentFunction, interval) as NodeJS.Timeout;
      }
    }, getPollingInterval());
  }, [onLoadMessages, onSilentRefresh, isAuthenticationValid]);

  // Setup optimized health check - much more conservative
  const setupHealthCheck = useCallback(() => {
    if (healthCheckRef.current) {
      clearInterval(healthCheckRef.current);
    }

    healthCheckRef.current = setInterval(() => {
      const timeSinceLastMessage = Date.now() - lastMessageTime.current;
      
      // Only trigger fallback after 10 minutes of true inactivity AND subscription issues
      if (timeSinceLastMessage > 600000 && !subscriptionHealthy) {
        if (setupFallbackPollingRef.current) {
          setupFallbackPollingRef.current();
        }
      }
    }, 120000); // Check every 2 minutes (much less frequent)
  }, [subscriptionHealthy]);

  // Reconnect subscription with circuit breaker protection
  const reconnectSubscription = useCallback((manual = false) => {
    // Throttle manual reconnects to prevent subscription storms from rapid clicks
    if (manual) {
      const now = Date.now();
      if (now - lastReconnectTimeRef.current < 5000) {
        console.log(`[${instanceIdRef.current}] ⏳ Manual reconnect throttled (5s cooldown)`);
        return;
      }
      lastReconnectTimeRef.current = now;
    }

    // Circuit breaker: if too many failures, go to fallback mode immediately
    if (!manual && circuitBreakerOpen) {
      console.log(`[${instanceIdRef.current}] 🚨 Circuit breaker is open, using fallback polling`);
      if (setupFallbackPollingRef.current) {
        setupFallbackPollingRef.current();
      }
      return;
    }

    if (!manual && retryCount >= maxRetries) {
      // Open circuit breaker after max retries
      setCircuitBreakerOpen(true);
      console.log(`[${instanceIdRef.current}] 🚨 Max retries reached, opening circuit breaker`);
      if (setupFallbackPollingRef.current) {
        setupFallbackPollingRef.current();
      }
      return;
    }

    // Reset retry count and circuit breaker on manual reconnect
    if (manual) {
      console.log(`[${instanceIdRef.current}] 🔄 Manual reconnect: resetting retry count and circuit breaker`);
      setRetryCount(0);
      setCircuitBreakerOpen(false);
      setConnectionErrors([]);
    } else {
      setRetryCount(prev => {
        const newCount = prev + 1;
        if (newCount >= circuitBreakerThreshold) {
          setCircuitBreakerOpen(true);
          console.log(`[${instanceIdRef.current}] 🚨 Circuit breaker threshold reached`);
        }
        return newCount;
      });
    }
    
    // Aggressive cleanup to prevent memory leaks
    if (channelRef.current) {
      try {
        supabase.removeChannel(channelRef.current);
      } catch (e) {
        // Ignore cleanup errors during emergency
      }
      channelRef.current = null;
    }
    
    // Force cleanup of polling intervals
    if (pollIntervalRef.current) {
      clearInterval(pollIntervalRef.current);
      pollIntervalRef.current = null;
    }
    
    // Exponential backoff (skip for manual)
    const delay = manual ? 0 : Math.min(1000 * Math.pow(2, retryCount), 30000);
    setTimeout(() => {
      if (setupRealtimeSubscriptionRef.current) {
        setupRealtimeSubscriptionRef.current();
      }
    }, delay);
  }, [retryCount, maxRetries, circuitBreakerOpen, circuitBreakerThreshold]);

  // CRITICAL: Setup realtime subscription WITH AUTHENTICATION GATE
  const setupRealtimeSubscription = useCallback(async () => {
    if (!tenantSchema || !enabled || !conversationId) {
      return;
    }
    
    // Check authentication before any Supabase realtime operations
    if (!isAuthenticationValid()) {
      console.log(`[${instanceIdRef.current}] 🔒 setupRealtimeSubscription blocked - authentication invalid`);
      return;
    }

    // Customer mode: stable channel key by customer; single-conv mode: by conversationId
    const subscriptionKey = customerIdProp
      ? `messages-customer-${customerIdProp}`
      : `messages-${conversationId}`;
    const existingChannel = activeSubscriptions.current.get(subscriptionKey);

    if (existingChannel) {
      try {
        supabase.removeChannel(existingChannel);
        activeSubscriptions.current.delete(subscriptionKey);
      } catch (error) {
        console.warn('Error removing duplicate subscription:', error);
      }
    }

    try {
      // Perform aggressive cleanup before creating new subscription
      aggressiveChannelCleanup();

      // Channel name and filter: customer mode vs single conversation mode
      const channelName = customerIdProp
        ? `messages-${tenantSchema}-customer-${customerIdProp}`
        : `messages-${tenantSchema}-${conversationId}`;
      const ids = allConversationIdsRef.current;
      let msgFilter: string;
      if (ids && ids.length > 0 && ids.length <= 50) {
        msgFilter = `conversation_id=in.(${ids.join(',')})`;
      } else {
        if (ids && ids.length > 50) {
          console.warn(`[${instanceIdRef.current}] ⚠️ in() filter too large (${ids.length} IDs > 50), falling back to single conversation filter`);
        }
        msgFilter = `conversation_id=eq.${conversationId}`;
      }

      const channel = supabase
        .channel(channelName)
        .on(
          'postgres_changes',
          {
            event: 'INSERT',
            schema: tenantSchema,
            table: 'messages',
            filter: msgFilter
          },
          (payload) => {
            console.log(`[${instanceIdRef.current}] 📨 Real-time INSERT received:`, {
              event: payload.eventType,
              table: payload.table,
              schema: payload.schema,
              messageId: payload.new?.id,
              conversationId: payload.new?.conversation_id,
              senderType: payload.new?.sender_type,
              content: payload.new?.content?.substring(0, 50) + '...'
            });
            handleRealtimeMessage(payload);
          }
        )
        .on(
          'postgres_changes',
          {
            event: 'UPDATE',
            schema: tenantSchema,
            table: 'messages',
            filter: msgFilter
          },
          (payload) => {
            console.log(`[${instanceIdRef.current}] 📝 Real-time UPDATE received:`, {
              event: payload.eventType,
              table: payload.table,
              schema: payload.schema,
              messageId: payload.new?.id,
              conversationId: payload.new?.conversation_id
            });
            handleRealtimeUpdate(payload);
          }
        )
        .subscribe((status, err) => {
          console.log(`[${instanceIdRef.current}] 🔌 Subscription status changed:`, { 
            status, 
            error: err?.toString(),
            conversationId,
            channelName 
          });
          
          if (status === 'SUBSCRIBED') {
            console.log(`[${instanceIdRef.current}] ✅ Real-time subscription ACTIVE for conversation ${conversationId}`);
            setSubscriptionHealthy(true);
            setRetryCount(0);
            consecutiveFailuresRef.current = 0;
            setConnectionErrors([]);
            setLastSuccessfulConnection(Date.now());
            
            // Clear any existing polling since real-time is working
            if (pollIntervalRef.current) {
              clearInterval(pollIntervalRef.current);
              pollIntervalRef.current = null;
              setFallbackPollingActive(false);
              console.log(`[${instanceIdRef.current}] 📡 BACKUP POLLING STOPPED - real-time subscription active`);
            }
          } else if (status === 'CHANNEL_ERROR') {
            console.error(`[${instanceIdRef.current}] ❌ Real-time subscription ERROR:`, err);
            setSubscriptionHealthy(false);
            
            // Track connection errors for Force Restart button visibility
            setConnectionErrors(prev => [...prev.slice(-4), `CHANNEL_ERROR at ${new Date().toISOString()}`]);
            
            // Check for JWT token expiry specifically
            if (err && (err.toString().includes('JWT') || err.toString().includes('expired') || err.toString().includes('invalid'))) {
              setAuthError('Token expired or invalid');

              // Refresh session BEFORE retrying subscription
              setTimeout(async () => {
                try {
                  const { error: refreshError } = await supabase.auth.refreshSession();
                  if (refreshError) {
                    setAuthError('Session expired — please refresh the page');
                    return;
                  }
                  setAuthError(null);
                  getTenantSchema();
                } catch {
                  setAuthError('Session expired — please refresh the page');
                }
              }, 2000);
              return;
            }
            
            // Retry with exponential backoff + jitter
            consecutiveFailuresRef.current++;

            // Max 5 retries before falling back to polling
            if (consecutiveFailuresRef.current > 5) {
              console.log(`[${instanceIdRef.current}] 🚨 Max channel retries (5) reached, falling back to polling`);
              if (setupFallbackPollingRef.current) {
                setupFallbackPollingRef.current();
              }
              return;
            }

            const baseDelay = Math.min(1000 * Math.pow(2, consecutiveFailuresRef.current), 30000);
            const jitter = Math.random() * baseDelay * 0.3;
            const backoffDelay = Math.round(baseDelay + jitter);

            console.log(`[${instanceIdRef.current}] ⏳ Retry ${consecutiveFailuresRef.current}/5 in ${backoffDelay}ms`);
            setTimeout(() => {
              if (setupRealtimeSubscriptionRef.current) {
                setupRealtimeSubscriptionRef.current();
              }
            }, backoffDelay);
          } else if (status === 'CLOSED') {
            setSubscriptionHealthy(false);
          }
        });

      // Store in singleton registry
      channelRef.current = channel;
      activeSubscriptions.current.set(subscriptionKey, channel);

    } catch (error) {
      console.error('Error setting up real-time subscription:', error);
      setSubscriptionHealthy(false);
    }
  }, [tenantSchema, enabled, conversationId, customerIdProp, allConversationIdsKey, handleRealtimeMessage, handleRealtimeUpdate, getTenantSchema, aggressiveChannelCleanup, isAuthenticationValid]);

  // CRITICAL: Enhanced cleanup function with singleton registry management
  const cleanup = useCallback(() => {
    if (cleanupExecutedRef.current) {
      return;
    }
    
    cleanupExecutedRef.current = true;
    
    // Clear auth listener
    if (authListenerRef.current) {
      authListenerRef.current.subscription?.unsubscribe();
      authListenerRef.current = null;
    }
    
    // CRITICAL: Clean up singleton registry (both possible key formats)
    const cleanupKeys = [
      `messages-${conversationId}`,
      ...(customerIdProp ? [`messages-customer-${customerIdProp}`] : [])
    ];
    for (const key of cleanupKeys) {
      const existingChannel = activeSubscriptions.current.get(key);
      if (existingChannel) {
        try {
          supabase.removeChannel(existingChannel);
          activeSubscriptions.current.delete(key);
        } catch (error) {
          console.warn('Error removing singleton subscription:', error);
        }
      }
    }
    
    // Clean up all subscriptions
    if (channelRef.current) {
      try {
        supabase.removeChannel(channelRef.current);
        channelRef.current = null;
      } catch (error) {
        console.warn('Error removing channelRef:', error);
      }
    }
    
    if (tenantChannelRef.current) {
      try {
        supabase.removeChannel(tenantChannelRef.current);
        tenantChannelRef.current = null;
      } catch (error) {
        console.warn('Error removing tenantChannelRef:', error);
      }
    }
    
    // Clear all intervals
    if (pollIntervalRef.current) {
      clearInterval(pollIntervalRef.current);
      pollIntervalRef.current = null;
    }
    
    if (healthCheckRef.current) {
      clearInterval(healthCheckRef.current);
      healthCheckRef.current = null;
    }
    
    // Reset state
    setSubscriptionHealthy(false);
    setFallbackPollingActive(false);
  }, [conversationId]);

  // Update function refs when functions change
  useEffect(() => {
    onMessageReceivedRef.current = onMessageReceived;
    onMessageUpdatedRef.current = onMessageUpdated;
    onLoadMessagesRef.current = onLoadMessages;
    onSilentRefreshRef.current = onSilentRefresh;
    onAIProcessingStartRef.current = onAIProcessingStart;
    setupFallbackPollingRef.current = setupFallbackPolling;
    reconnectSubscriptionRef.current = reconnectSubscription;
    setupHealthCheckRef.current = setupHealthCheck;
    setupRealtimeSubscriptionRef.current = setupRealtimeSubscription;
  }, [onMessageReceived, onMessageUpdated, onLoadMessages, onSilentRefresh, onAIProcessingStart, setupFallbackPolling, reconnectSubscription, setupHealthCheck, setupRealtimeSubscription]);

  // Initialize tenant schema on mount
  useEffect(() => {
    getTenantSchema();
  }, [getTenantSchema]);

  // CRITICAL: Enhanced effect for conversation changes WITH AUTHENTICATION GATE
  useEffect(() => {
    if (conversationId && tenantSchema) {
      // Check authentication before any operations
      if (!isAuthenticationValid()) {
        console.log(`[${instanceIdRef.current}] 🔒 Conversation setup blocked - authentication invalid`);
        return;
      }
      
      // Reset cleanup flag for new conversation
      cleanupExecutedRef.current = false;

      // ✅ Removed onLoadMessages() call - RealtimeChat handles initial load
      // Real-time updates come via subscription, not by calling loadMessages

      // Reset state for new conversation
      setRetryCount(0);
      setAuthError(null);
      setIsTransitioning(false);

      // [FIX: MESSAGE-MIXING] Setup subscription synchronously (no delay)
      // Phase 2.5 stabilized callbacks → no cascading cleanup → delay unnecessary
      // This eliminates the 100ms gap window where messages could be mishandled
      if (setupRealtimeSubscriptionRef.current) {
        console.log(`[${instanceIdRef.current}] 🔌 [FIX: MESSAGE-MIXING] Setting up subscription synchronously for conversation:`, conversationId);
        setupRealtimeSubscriptionRef.current();
      }

      // No cleanup needed here - handled by subscription cleanup effect
    }
  }, [conversationId, tenantSchema, onLoadMessages, isAuthenticationValid]);

  // [FIX: NEW-CONV-MISS] Re-create subscription + catch-up fetch when allConversationIds changes
  // When a new conversation is created mid-session, the Supabase realtime filter (conversation_id=in.(...))
  // doesn't include the new conv → AI reply INSERT is dropped server-side. This effect detects the change
  // and re-subscribes with the updated filter, plus a catch-up fetch for messages missed during the gap.
  const prevAllConversationIdsKeyRef = useRef<string>(allConversationIdsKey);
  useEffect(() => {
    if (prevAllConversationIdsKeyRef.current === allConversationIdsKey) return;

    const prevKey = prevAllConversationIdsKeyRef.current;
    prevAllConversationIdsKeyRef.current = allConversationIdsKey;

    if (!tenantSchema || !enabled) return;
    // Skip initial load (empty → populated)
    if (!prevKey && allConversationIdsKey) return;

    if (import.meta.env.DEV) console.log(`[${instanceIdRef.current}] 🆕 [NEW-CONV-MISS] allConversationIds changed, re-subscribing + catch-up fetch`);

    // 1. Re-create subscription with updated in() filter
    if (setupRealtimeSubscriptionRef.current) {
      setupRealtimeSubscriptionRef.current();
    }

    // 2. Catch-up fetch to pick up messages missed during the gap
    setTimeout(() => {
      if (onLoadMessagesRef.current) {
        onLoadMessagesRef.current();
      }
    }, 500);
  }, [allConversationIdsKey, tenantSchema, enabled]);

  // Effect for cleanup on unmount or when disabled - enhanced with conversation tracking
  useEffect(() => {
    const currentConversationId = conversationId;
    return () => {
      // Only cleanup if we're truly unmounting, not just changing conversations
      if (currentConversationId) {
        cleanup();
      }
    };
  }, [cleanup, conversationId]);

  // Effect for handling auth changes - enhanced
  useEffect(() => {
    if (!authListenerRef.current) {
      authListenerRef.current = supabase.auth.onAuthStateChange((event, session) => {
        if (event === 'SIGNED_OUT') {
          cleanup();
        } else if (event === 'SIGNED_IN' || event === 'TOKEN_REFRESHED') {
          setAuthError(null);

          // Only reset if we don't have a valid tenant schema
          // (prevents unnecessary reset on tab focus)
          if (!tenantSchema) {
            console.log('[useRealtimeMessages] No tenant schema, initializing...');
            // Reset schema initialization to force re-fetch
            schemaInitialized.current = false;
            setTenantSchema(null);

            // Re-initialize with delay
            setTimeout(() => {
              getTenantSchema();
            }, 1000);
          } else {
            console.log('[useRealtimeMessages] Tenant schema already set, skipping re-init');
          }
        }
      });
    }
  }, [cleanup, getTenantSchema]);

  // Phase 2.5: Wrap return value in useMemo to prevent external cleanup triggers
  // This ensures the returned object has a stable reference across renders
  return useMemo(() => ({
    subscriptionHealthy,
    retryCount,
    authError,
    isTransitioning,
    connectionErrors,
    lastSuccessfulConnection,
    fallbackPollingActive,
    addRecentlySentMessage,
    dataQualityScore,
    tenantSchema,
    poorQualityCount,
    cleanup,
    forceReconnect: () => reconnectSubscription(true),
    enableFallbackMode: setupFallbackPolling,
    forceCleanup: cleanup,
    forceSubscriptionRestart: () => reconnectSubscription(true)
  }), [
    subscriptionHealthy,
    retryCount,
    authError,
    isTransitioning,
    connectionErrors,
    lastSuccessfulConnection,
    fallbackPollingActive,
    addRecentlySentMessage,
    dataQualityScore,
    tenantSchema,
    poorQualityCount,
    cleanup,
    reconnectSubscription,
    setupFallbackPolling
  ]);
}